/**
 * Frontend Keep-Alive Utility
 * 
 * Automatically pings the Supabase database whenever a visitor loads the site.
 * Throttled to once every 24 hours per browser via localStorage to minimize
 * unnecessary traffic while ensuring the 7-day inactivity pause counter is reset.
 */

import { supabase } from "@/lib/supabase";

const KEEPALIVE_KEY = "supabase_keepalive_last";
const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

export async function pingSupabaseFromClient(): Promise<void> {
  if (typeof window === "undefined") return;

  try {
    const lastPing = localStorage.getItem(KEEPALIVE_KEY);
    const now = Date.now();

    // Only ping if at least 24 hours have passed since last ping from this browser
    if (lastPing && now - Number(lastPing) < TWENTY_FOUR_HOURS_MS) {
      return;
    }

    const { error } = await supabase
      .from("products")
      .select("id")
      .limit(1);

    if (!error) {
      localStorage.setItem(KEEPALIVE_KEY, now.toString());
      if (import.meta.env.DEV) {
        console.debug("[KeepAlive] Supabase pinged successfully from frontend.");
      }
    }
  } catch (err) {
    // Non-blocking background call: fail silently so UI is unaffected
    if (import.meta.env.DEV) {
      console.debug("[KeepAlive] Supabase ping failed:", err);
    }
  }
}
